import type { StateCreator } from 'zustand'
import type {
  AppFixedInfo,
  AppVersion,
  DisplayInfo,
  MainPackageInfo,
  PlatformPackage,
  VersionStatus,
} from '@/types'
import { DEFAULT_APP_ICON } from '@/lib/appIcon'
import { DEFAULT_CONTENT_LANGUAGE } from '@/pages/app-detail/versions/languages'
import {
  compareVersionDesc,
  hasEverReleased,
  resolveHistoricalPlatformPackages,
} from '@/pages/app-detail/versions/platform'
import { mergeDisplayInfo } from '@/pages/app-detail/versions/drawer/formModel'
import { placeholderApp } from './appsSlice'
import { seedAppConfig } from './configSlice'
import {
  hasPlatformPackagesReplaced,
  keepLocalPackageMeta,
  markPlatformPackagesReplaced,
  mergeReplacedPlatformPackages,
} from './packageMeta'
import { stamp } from './shared'
import type { AppState } from './types'

/** 版本列表、草稿与状态流转 */
export interface VersionsSlice {
  versions: Record<string, AppVersion[]>
  /** 版本列表接口给的已上线最高版本号（按 app_id） */
  maxOnlineVersion: Record<string, string | undefined>
  /** 版本列表页监听此计数以重新拉取（状态变更类写操作后 bump） */
  versionsListRev: number

  /** 仅 bump 刷新计数；整表刷新路径用，不改本地行状态 */
  bumpVersionsListRev: () => void
  setMaxOnlineVersion: (app_id: string, version: string | undefined) => void

  deleteVersion: (app_id: string, version_id: string) => void
  withdrawVersion: (app_id: string, version_id: string) => void
  cancelSchedule: (app_id: string, version_id: string) => void
  releaseNow: (app_id: string, version_id: string) => void
  delistVersion: (app_id: string, version_id: string) => void
  disableVersion: (app_id: string, version_id: string) => void
  enableVersion: (app_id: string, version_id: string) => void
  relistVersion: (app_id: string, version_id: string) => void
  rollbackVersion: (app_id: string, version_id: string) => void

  createDraftVersion: (app_id: string, main_package: MainPackageInfo) => AppVersion
  /** 上传主包落草稿后，把远端 app_id / version 写入本地缓存 */
  ensureRemoteDraft: (input: {
    app_id: string
    appName?: string
    icon?: string
    version: AppVersion
    /** 主包上传响应是子包真相，不再把上一份主包的本地子包合并回来 */
    replacePlatformPackages?: boolean
  }) => void
  /**
   * 版本列表回填。同 id 保留本地已拉的详情字段。
   * `replace` 仅在本次已是全量列表时使用，丢掉不在响应里的旧记录。
   * 分页片段必须合并，避免后返回的第一页盖掉已经写入的其余版本。
   */
  syncVersions: (
    app_id: string,
    list: AppVersion[],
    options?: { replace?: boolean },
  ) => void
  saveVersion: (
    app_id: string,
    version_id: string,
    patch: Partial<AppVersion>,
  ) => void
  /** 平台包上传成功后按上传响应写入（替换同平台项） */
  upsertPlatformPackage: (
    app_id: string,
    version_id: string,
    pkg: Omit<PlatformPackage, 'package_version'> & { package_version?: string },
  ) => void
  /** 提审成功后按接口响应写入状态（免审可能直接 scheduled / released） */
  submitVersion: (
    app_id: string,
    version_id: string,
    result: Pick<
      AppVersion,
      'status' | 'scheduled_at' | 'released_at' | 'scope_snapshot'
    >,
  ) => void
}

function patchVersion(
  list: AppVersion[],
  version_id: string,
  patch: Partial<AppVersion>,
): AppVersion[] {
  return list.map((v) => (v.id === version_id ? { ...v, ...patch } : v))
}

function setVersionStatus(
  state: Pick<VersionsSlice, 'versions' | 'versionsListRev'>,
  app_id: string,
  version_id: string,
  status: VersionStatus,
  extra: Partial<AppVersion> = {},
) {
  const list = state.versions[app_id] ?? []
  return {
    versions: {
      ...state.versions,
      [app_id]: patchVersion(list, version_id, {
        status,
        last_operated_at: stamp(),
        ...extra,
      }),
    },
    versionsListRev: state.versionsListRev + 1,
  }
}

export const createVersionsSlice: StateCreator<AppState, [], [], VersionsSlice> = (
  set,
  get,
) => ({
  versions: {},
  maxOnlineVersion: {},
  versionsListRev: 0,

  bumpVersionsListRev: () =>
    set((state) => ({ versionsListRev: state.versionsListRev + 1 })),

  setMaxOnlineVersion: (app_id, version) =>
    set((state) =>
      state.maxOnlineVersion[app_id] === version
        ? state
        : { maxOnlineVersion: { ...state.maxOnlineVersion, [app_id]: version } },
    ),

  deleteVersion: (app_id, version_id) =>
    set((state) => ({
      versions: {
        ...state.versions,
        [app_id]: (state.versions[app_id] ?? []).filter((v) => v.id !== version_id),
      },
      versionsListRev: state.versionsListRev + 1,
    })),

  withdrawVersion: (app_id, version_id) =>
    set((state) => setVersionStatus(state, app_id, version_id, 'developing')),

  // 取消定时保留过审结论：变为已下线（从未上线），之后可立即上线或重新定时
  cancelSchedule: (app_id, version_id) =>
    set((state) =>
      setVersionStatus(state, app_id, version_id, 'archived', {
        scheduled_at: null,
      }),
    ),

  // 会顶替线上版本：确认弹窗走 refreshAppVersions，不在此乐观改行
  releaseNow: (app_id, version_id) =>
    set((state) => {
      const target = (state.versions[app_id] ?? []).find((v) => v.id === version_id)
      if (!target) return {}
      return setVersionStatus(state, app_id, version_id, 'released', {
        released_at: target.released_at ?? stamp(),
        scheduled_at: null,
      })
    }),

  delistVersion: (app_id, version_id) =>
    set((state) => setVersionStatus(state, app_id, version_id, 'delisted')),

  disableVersion: (app_id, version_id) =>
    set((state) => setVersionStatus(state, app_id, version_id, 'disabled')),

  // 会顶替线上版本：确认弹窗走 refreshAppVersions，不在此乐观改行
  enableVersion: (app_id, version_id) =>
    set((state) => setVersionStatus(state, app_id, version_id, 'released')),

  relistVersion: (app_id, version_id) =>
    set((state) => setVersionStatus(state, app_id, version_id, 'released')),

  rollbackVersion: (app_id, version_id) =>
    set((state) => setVersionStatus(state, app_id, version_id, 'released')),

  createDraftVersion: (app_id, main_package) => {
    const list = get().versions[app_id] ?? []
    const hasReleased = list.some(hasEverReleased)
    const lastReleased = list
      .filter((v) => v.status === 'released')
      .sort((a, b) => compareVersionDesc(a.version, b.version))[0]

    // 展示信息只带必填骨架：支持语言（英语）+ 空的一句话介绍，不预填可选字段
    const display_info: DisplayInfo = {
      supported_languages: [DEFAULT_CONTENT_LANGUAGE],
      default_language: DEFAULT_CONTENT_LANGUAGE,
      categories: [],
      tags: [],
      localizations: {
        [DEFAULT_CONTENT_LANGUAGE]: {
          tagline: '',
          description: '',
          pc_screenshots: [],
          mobile_screenshots: [],
        },
      },
    }

    const app_fixed_info: AppFixedInfo = lastReleased?.app_fixed_info
      ? { ...lastReleased.app_fixed_info }
      : {
          distribution_type: 'self_built',
          listing_space_types: ['workspace', 'personal'],
          owner_workspace: '研发空间',
          available_type: 'all',
          eligible_members: [],
          eligible_departments: [],
        }

    const version: AppVersion = {
      id: `ver_${Date.now()}`,
      version: main_package.version_code,
      status: 'developing',
      platforms: main_package.platforms,
      released_at: null,
      last_operated_at: stamp(),
      scheduled_at: null,
      is_first_version: !hasReleased,
      main_package,
      platform_packages: main_package.needs_platform_packages
        ? resolveHistoricalPlatformPackages(list, main_package.platforms)
        : main_package.platforms.map((platform) => ({
            platform,
            file_name: main_package.file_name,
            size_mb: main_package.size_mb,
            package_version: main_package.version_code,
            is_historical: false,
          })),
      changelog: {},
      app_fixed_info,
      display_info,
    }
    set((state) => ({
      versions: {
        ...state.versions,
        [app_id]: [version, ...(state.versions[app_id] ?? [])],
      },
    }))
    return version
  },

  ensureRemoteDraft: ({
    app_id,
    appName,
    icon,
    version,
    replacePlatformPackages,
  }) => {
    set((state) => {
      const hasApp = state.apps.some((a) => a.id === app_id)
      // 已有应用的 name 是平台展示名，不用安装包里的客户端名覆盖
      const apps = hasApp
        ? state.apps.map((a) =>
            a.id === app_id ? { ...a, icon: icon ?? a.icon } : a,
          )
        : [
            ...state.apps,
            {
              ...placeholderApp(app_id),
              name: appName ?? app_id,
              icon: icon ?? DEFAULT_APP_ICON,
            },
          ]

      if (replacePlatformPackages) markPlatformPackagesReplaced(version.id)
      const declared =
        version.main_package?.platforms ?? version.platforms ?? []
      const platform_packages = replacePlatformPackages
        ? (version.platform_packages ?? [])
        : hasPlatformPackagesReplaced(version.id)
          ? mergeReplacedPlatformPackages(
              (state.versions[app_id] ?? []).find((v) => v.id === version.id)
                ?.platform_packages,
              version.platform_packages,
              declared,
            )
          : keepLocalPackageMeta(
              (state.versions[app_id] ?? []).find((v) => v.id === version.id)
                ?.platform_packages,
              version.platform_packages,
              declared,
            )

      const list = state.versions[app_id] ?? []
      const idx = list.findIndex((v) => v.id === version.id)
      const versions =
        idx >= 0
          ? list.map((v, i) =>
              i === idx
                ? {
                    ...v,
                    ...version,
                    platform_packages,
                  }
                : v,
            )
          : [{ ...version, platform_packages }, ...list]

      return {
        apps,
        versions: { ...state.versions, [app_id]: versions },
        ...seedAppConfig(state, app_id),
      }
    })
  },

  syncVersions: (app_id, list, options) => {
    set((state) => {
      const prev = state.versions[app_id] ?? []
      const byId = new Map(prev.map((v) => [v.id, v]))
      for (const item of list) {
        const old = byId.get(item.id)
        byId.set(
          item.id,
          old
            ? {
                ...old,
                ...item,
                main_package: old.main_package ?? item.main_package,
                platform_packages: old.platform_packages ?? item.platform_packages,
                uploaded_platforms:
                  item.uploaded_platforms ?? old.uploaded_platforms,
                display_info: mergeDisplayInfo(
                  item.display_info,
                  old.display_info,
                ),
                app_fixed_info: old.app_fixed_info ?? item.app_fixed_info,
                changelog: old.changelog ?? item.changelog,
                scope_snapshot: old.scope_snapshot ?? item.scope_snapshot,
                permission_diff: item.permission_diff ?? old.permission_diff,
                event_diff: item.event_diff ?? old.event_diff,
                is_first_version: old.is_first_version ?? item.is_first_version,
                review_comment: item.review_comment ?? old.review_comment,
              }
            : item,
        )
      }
      const incomingIds = new Set(list.map((v) => v.id))
      const rest = options?.replace
        ? []
        : prev.filter((v) => !incomingIds.has(v.id))
      const merged = [...list.map((item) => byId.get(item.id)!), ...rest]

      // 浅字段无变化则跳过；key 不存在时仍要写入（含 []），避免页面 selector 读到 undefined
      const keyExists = Object.prototype.hasOwnProperty.call(
        state.versions,
        app_id,
      )
      if (
        keyExists &&
        merged.length === prev.length &&
        merged.every((v, i) => {
          const p = prev[i]
          return (
            p &&
            p.id === v.id &&
            p.version === v.version &&
            p.status === v.status &&
            p.last_operated_at === v.last_operated_at &&
            p.scheduled_at === v.scheduled_at &&
            p.released_at === v.released_at &&
            (p.platforms?.join() ?? '') === (v.platforms?.join() ?? '')
          )
        })
      ) {
        return state
      }

      return {
        versions: { ...state.versions, [app_id]: merged },
      }
    })
  },

  saveVersion: (app_id, version_id, patch) =>
    set((state) => {
      const list = state.versions[app_id] ?? []
      return {
        versions: {
          ...state.versions,
          [app_id]: patchVersion(list, version_id, {
            ...patch,
            last_operated_at: stamp(),
          }),
        },
      }
    }),

  upsertPlatformPackage: (app_id, version_id, pkg) =>
    set((state) => {
      const list = state.versions[app_id] ?? []
      const version = list.find((v) => v.id === version_id)
      if (!version) return state
      const next: PlatformPackage = {
        ...pkg,
        package_version: pkg.package_version ?? version.version,
      }
      const platform_packages = [
        ...(version.platform_packages ?? []).filter(
          (p) => p.platform !== pkg.platform,
        ),
        next,
      ]
      return {
        versions: {
          ...state.versions,
          [app_id]: patchVersion(list, version_id, {
            platform_packages,
            last_operated_at: stamp(),
          }),
        },
      }
    }),

  submitVersion: (app_id, version_id, result) =>
    set((state) => {
      const approved = (state.scopes[app_id] ?? [])
        .filter((s) => s.status === 'approved')
        .map((s) => s.id)
      return setVersionStatus(state, app_id, version_id, result.status, {
        scheduled_at: result.scheduled_at,
        released_at: result.released_at,
        scope_snapshot: result.scope_snapshot ?? { approved },
      })
    }),
})
